import React, { useState, useEffect } from 'react';
import { Search, X, Image, Loader2, ExternalLink, Trash2, Upload, Plus, Images, FolderOpen } from 'lucide-react';
import { listMedia, uploadMedia, type MediaItem } from '../lib/mediaLibrary';

interface UploadedImage {
  id: string;
  url: string;
  name: string;
  uploadedAt: Date;
}

interface StockPhotoBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectImage: (imageUrl: string) => void;
  uploadedImages?: UploadedImage[];
  onDeleteImage?: (imageId: string) => void;
  onUploadImages?: () => void;
  isUploading?: boolean;
  initialTab?: 'my-photos' | 'media-library' | 'stock';
  /** When set, shows the Media Library tab with business media. */
  businessId?: string | null;
}

type PhotoTab = 'my-photos' | 'media-library' | 'stock';


const DEMO_IMAGES: Record<string, string[]> = {
  cars: [
    'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800',
    'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800',
    'https://images.unsplash.com/photo-1542362567-b07e54358753?w=800',
    'https://images.unsplash.com/photo-1553440569-bcc63803a83d?w=800',
    'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800',
    'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=800',
    'https://images.unsplash.com/photo-1514316454349-750a7fd3da3a?w=800',
    'https://images.unsplash.com/photo-1525609004556-c46c7d6cf023?w=800',
    'https://images.unsplash.com/photo-1502877338535-766e1452684a?w=800',
    'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800',
    'https://images.unsplash.com/photo-1511919884226-fd3cad34687c?w=800',
    'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800',
    'https://images.unsplash.com/photo-1563720223185-11003d516935?w=800',
    'https://images.unsplash.com/photo-1555215858-9dc342046e4e?w=800',
    'https://images.unsplash.com/photo-1567818735868-e71b99932e29?w=800',
    'https://images.unsplash.com/photo-1573950940509-d924ee3fd345?w=800',
    'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=800',
    'https://images.unsplash.com/photo-1546768292-fb12f6c92568?w=800',
    'https://images.unsplash.com/photo-1563720360172-67b8f3dce741?w=800',
    'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=800',
  ],
  detailing: [
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800',
    'https://images.unsplash.com/photo-1601362840469-51e4d8d58785?w=800',
    'https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?w=800',
    'https://images.unsplash.com/photo-1594051673969-172a2ab7f670?w=800',
    'https://images.unsplash.com/photo-1600661660042-94b0e5264b17?w=800',
    'https://images.unsplash.com/photo-1607860108855-64acf2078987?w=800',
    'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=800',
    'https://images.unsplash.com/photo-1590362891991-f776e747a588?w=800',
    'https://images.unsplash.com/photo-1594736797933-d0501ba2fe65?w=800',
    'https://images.unsplash.com/photo-1599705565698-0800902eb19f?w=800',
    'https://images.unsplash.com/photo-1603386329225-868f9b1ee6c9?w=800',
    'https://images.unsplash.com/photo-1571607388263-1044f9ea01dd?w=800',
    'https://images.unsplash.com/photo-1606220838315-056192d5e927?w=800',
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
    'https://images.unsplash.com/photo-1599256621730-0b7f83ca1c8f?w=800',
    'https://images.unsplash.com/photo-1625231334168-35067f8853ed?w=800',
  ],
  automotive: [
    'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800',
    'https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=800',
    'https://images.unsplash.com/photo-1565043666747-69f6646db940?w=800',
    'https://images.unsplash.com/photo-1619405399517-d7fce0f13302?w=800',
    'https://images.unsplash.com/photo-1486262638829-099e61a77404?w=800',
    'https://images.unsplash.com/photo-1530046339160-ce3e530c7d2f?w=800',
    'https://images.unsplash.com/photo-1616422285623-13ff0162193c?w=800',
    'https://images.unsplash.com/photo-1564844536308-50b114a1d946?w=800',
    'https://images.unsplash.com/photo-1625047509248-ec889cbff17f?w=800',
    'https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=800',
  ],
  showroom: [
    'https://images.unsplash.com/photo-1562113285-6cbaa7e392f5?w=800',
    'https://images.unsplash.com/photo-1567808291548-fc3ee04dbcf0?w=800',
    'https://images.unsplash.com/photo-1616788494707-ec28f08d05a1?w=800',
    'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800',
    'https://images.unsplash.com/photo-1549927681-0b673b8243ab?w=800',
    'https://images.unsplash.com/photo-1580274455191-1c62238fa333?w=800',
    'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800',
    'https://images.unsplash.com/photo-1594738453259-7b8dbb7f8b0f?w=800',
  ],
  wrapping: [
    'https://images.unsplash.com/photo-1565733293285-77aa342b22dd?w=800',
    'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=800',
    'https://images.unsplash.com/photo-1609003050270-a69fec72f2b9?w=800',
    'https://images.unsplash.com/photo-1610647752706-3bb12232b3ab?w=800',
    'https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?w=800',
    'https://images.unsplash.com/photo-1600705722908-bab1e61c0b4d?w=800',
    'https://images.unsplash.com/photo-1632823469850-2f77dd9c7f93?w=800',
    'https://images.unsplash.com/photo-1580894732444-8ecded7900cd?w=800',
  ],
  tinting: [
    'https://images.unsplash.com/photo-1570733117311-d990c3816c47?w=800',
    'https://images.unsplash.com/photo-1607860108855-64acf2078987?w=800',
    'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=800',
    'https://images.unsplash.com/photo-1625047509168-a7026f36de04?w=800',
    'https://images.unsplash.com/photo-1606220945770-b5b6c2c55bf1?w=800',
    'https://images.unsplash.com/photo-1598084991519-c90900bc9df0?w=800',
  ],
  business: [
    'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800',
    'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800',
    'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800',
    'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800',
    'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800',
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800',
  ],
  nature: [
    'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800',
    'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=800',
    'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800',
    'https://images.unsplash.com/photo-1426604966848-d7adac402bff?w=800',
    'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=800',
    'https://images.unsplash.com/photo-1475924156734-496f6cac6ec1?w=800',
  ],
  abstract: [
    'https://images.unsplash.com/photo-1557683316-973673baf926?w=800',
    'https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=800',
    'https://images.unsplash.com/photo-1557682224-5b8590cd9ec5?w=800',
    'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=800',
    'https://images.unsplash.com/photo-1567095761054-7a02e69e5c43?w=800',
  ],
  celebration: [
    'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800',
    'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=800',
    'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=800',
    'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=800',
    'https://images.unsplash.com/photo-1464207687429-7505649dae38?w=800',
  ],
  sale: [
    'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=800',
    'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800',
    'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=800',
    'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=800',
  ],
  service: [
    'https://images.unsplash.com/photo-1556745757-8d76bdb6984b?w=800',
    'https://images.unsplash.com/photo-1560264418-c4445382edbc?w=800',
    'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800',
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
    'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800',
  ]
};

const CATEGORIES = [
  { id: 'all', label: 'All', icon: '??' },
  { id: 'cars', label: 'Cars', icon: '??' },
  { id: 'detailing', label: 'Detailing', icon: '?' },
  { id: 'wrapping', label: 'Vinyl Wrap', icon: '??' },
  { id: 'tinting', label: 'Tinting', icon: '??' },
  { id: 'automotive', label: 'Automotive', icon: '??' },
  { id: 'showroom', label: 'Showroom', icon: '??' },
  { id: 'business', label: 'Business', icon: '??' },
  { id: 'service', label: 'Service', icon: '???' },
  { id: 'sale', label: 'Sales', icon: '???' },
  { id: 'nature', label: 'Nature', icon: '??' },
  { id: 'celebration', label: 'Events', icon: '??' },
  { id: 'abstract', label: 'Abstract', icon: '??' },
];

export default function StockPhotoBrowser({
  isOpen,
  onClose,
  onSelectImage,
  uploadedImages = [],
  onDeleteImage,
  onUploadImages,
  isUploading = false,
  initialTab = 'my-photos',
  businessId = null,
}: StockPhotoBrowserProps) {
  const [activeTab, setActiveTab] = useState<PhotoTab>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [stockImages, setStockImages] = useState<string[]>([]);
  const [isLoadingStock, setIsLoadingStock] = useState(false);
  const [libraryItems, setLibraryItems] = useState<MediaItem[]>([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [isUploadingLibrary, setIsUploadingLibrary] = useState(false);
  const libraryFileInputRef = React.useRef<HTMLInputElement>(null);

  const showMediaLibrary = Boolean(businessId);

  useEffect(() => {
    if (isOpen) {
      const preferred: PhotoTab =
        uploadedImages.length > 0
          ? 'my-photos'
          : showMediaLibrary
            ? 'media-library'
            : 'stock';
      setActiveTab(initialTab === 'stock' ? 'stock' : preferred);
      setSearchQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (activeTab === 'stock') {
      loadStockImages();
    }
  }, [selectedCategory, activeTab]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'media-library' || !businessId) return;
    let cancelled = false;
    setIsLoadingLibrary(true);
    listMedia(businessId)
      .then((items) => {
        if (!cancelled) setLibraryItems(items);
      })
      .catch((err) => {
        console.error('Failed to load media library:', err);
        if (!cancelled) setLibraryItems([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingLibrary(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, activeTab, businessId]);

  const loadStockImages = () => {
    setIsLoadingStock(true);
    setTimeout(() => {
      let allImages: string[] = [];
      if (selectedCategory === 'all') {
        Object.values(DEMO_IMAGES).forEach(imgs => { allImages = [...allImages, ...imgs]; });
      } else {
        allImages = DEMO_IMAGES[selectedCategory] || [];
      }
      setStockImages(allImages);
      setIsLoadingStock(false);
    }, 300);
  };

  const handleSelectImage = (imageUrl: string) => {
    onSelectImage(imageUrl);
    onClose();
  };

  const handleLibraryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';
    if (!businessId || files.length === 0) return;
    setIsUploadingLibrary(true);
    try {
      const created = await uploadMedia(businessId, files);
      setLibraryItems((prev) => [...created, ...prev]);
    } catch (err) {
      console.error('Media library upload failed:', err);
    } finally {
      setIsUploadingLibrary(false);
    }
  };

  const filteredUploaded = uploadedImages.filter(img =>
    img.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredLibrary = libraryItems.filter(
    (item) =>
      searchQuery.trim() === '' ||
      (item.file_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.alt || '').toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex flex-col"
      style={{
        zIndex: 100060,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-white flex flex-col overflow-hidden w-full h-full sm:mx-auto sm:my-4 sm:max-w-5xl sm:max-h-[90vh] sm:rounded-2xl sm:h-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200">
          <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Photos</h2>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation"
              style={{ minWidth: '44px', minHeight: '44px' }}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Tabs */}
          <div className="px-4 sm:px-6 flex gap-1 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            <button
              onClick={() => { setActiveTab('my-photos'); setSearchQuery(''); }}
              className={`flex-1 sm:flex-none px-4 py-2.5 text-sm font-semibold rounded-t-xl transition-colors touch-manipulation whitespace-nowrap ${
                activeTab === 'my-photos'
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <Images className="h-4 w-4" />
                My Photos
                {uploadedImages.length > 0 && (
                  <span className="bg-blue-100 text-blue-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{uploadedImages.length}</span>
                )}
              </span>
            </button>
            {showMediaLibrary && (
              <button
                onClick={() => { setActiveTab('media-library'); setSearchQuery(''); }}
                className={`flex-1 sm:flex-none px-4 py-2.5 text-sm font-semibold rounded-t-xl transition-colors touch-manipulation whitespace-nowrap ${
                  activeTab === 'media-library'
                    ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center justify-center gap-1.5">
                  <FolderOpen className="h-4 w-4" />
                  Media Library
                  {libraryItems.length > 0 && activeTab === 'media-library' && (
                    <span className="bg-blue-100 text-blue-700 text-xs font-bold px-1.5 py-0.5 rounded-full">
                      {libraryItems.length}
                    </span>
                  )}
                </span>
              </button>
            )}
            <button
              onClick={() => { setActiveTab('stock'); setSearchQuery(''); }}
              className={`flex-1 sm:flex-none px-4 py-2.5 text-sm font-semibold rounded-t-xl transition-colors touch-manipulation whitespace-nowrap ${
                activeTab === 'stock'
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                <Image className="h-4 w-4" />
                Stock Photos
              </span>
            </button>
          </div>
        </div>

        {/* My Photos Tab */}
        {activeTab === 'my-photos' && (
          <>
            <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-b border-gray-100 flex items-center gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search your photos..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm touch-manipulation"
                  style={{ fontSize: '16px' }}
                />
              </div>
              {onUploadImages && (
                <button
                  onClick={onUploadImages}
                  disabled={isUploading}
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center gap-1.5 font-medium text-sm touch-manipulation flex-shrink-0"
                  style={{ minHeight: '44px' }}
                >
                  {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  <span className="hidden sm:inline">{isUploading ? 'Uploading...' : 'Upload'}</span>
                </button>
              )}
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
              {filteredUploaded.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
                    <Images className="h-8 w-8 text-blue-400" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    {searchQuery ? 'No photos found' : 'No photos yet'}
                  </h3>
                  <p className="text-sm text-gray-500 mb-5 max-w-xs">
                    {searchQuery ? 'Try a different search term.' : 'Upload photos to use in your email templates.'}
                  </p>
                  {!searchQuery && onUploadImages && (
                    <button
                      onClick={onUploadImages}
                      disabled={isUploading}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center gap-2 font-medium text-sm touch-manipulation"
                      style={{ minHeight: '44px' }}
                    >
                      {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      Upload Photos
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {filteredUploaded.map((image) => (
                    <div
                      key={image.id}
                      onClick={() => handleSelectImage(image.url)}
                      className="group relative rounded-xl overflow-hidden border-2 border-gray-200 hover:border-blue-500 active:border-blue-600 transition-all cursor-pointer aspect-square touch-manipulation"
                    >
                      <img src={image.url} alt={image.name} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-2.5">
                          <p className="text-white text-xs font-medium truncate">{image.name}</p>
                        </div>
                        {onDeleteImage && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onDeleteImage(image.id); }}
                            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 touch-manipulation"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <div className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">Select</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Media Library Tab */}
        {activeTab === 'media-library' && showMediaLibrary && (
          <>
            <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-b border-gray-100 flex items-center gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search media library..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm touch-manipulation"
                  style={{ fontSize: '16px' }}
                />
              </div>
              <button
                onClick={() => libraryFileInputRef.current?.click()}
                disabled={isUploadingLibrary}
                className="px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center gap-1.5 font-medium text-sm touch-manipulation flex-shrink-0"
                style={{ minHeight: '44px' }}
              >
                {isUploadingLibrary ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">
                  {isUploadingLibrary ? 'Uploading...' : 'Upload'}
                </span>
              </button>
              <input
                ref={libraryFileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleLibraryUpload}
                className="hidden"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
              {isLoadingLibrary ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                </div>
              ) : filteredLibrary.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
                    <FolderOpen className="h-8 w-8 text-blue-400" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    {searchQuery ? 'No media found' : 'No media yet'}
                  </h3>
                  <p className="text-sm text-gray-500 mb-5 max-w-xs">
                    {searchQuery
                      ? 'Try a different search term.'
                      : 'Upload images to your media library to reuse them across campaigns and templates.'}
                  </p>
                  {!searchQuery && (
                    <button
                      onClick={() => libraryFileInputRef.current?.click()}
                      disabled={isUploadingLibrary}
                      className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center gap-2 font-medium text-sm touch-manipulation"
                      style={{ minHeight: '44px' }}
                    >
                      {isUploadingLibrary ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      Upload Media
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {filteredLibrary.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleSelectImage(item.url)}
                      className="group relative rounded-xl overflow-hidden border-2 border-gray-200 hover:border-blue-500 active:border-blue-600 transition-all cursor-pointer aspect-square touch-manipulation"
                    >
                      <img
                        src={item.url}
                        alt={item.alt || item.file_name || 'Media'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-2.5">
                          <p className="text-white text-xs font-medium truncate">
                            {item.file_name || 'Untitled'}
                          </p>
                        </div>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <div className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                          Select
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Stock Photos Tab */}
        {activeTab === 'stock' && (
          <>
            <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search stock photos..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm touch-manipulation"
                  style={{ fontSize: '16px' }}
                />
              </div>
            </div>

            {/* Categories */}
            <div className="flex-shrink-0 border-b border-gray-100">
              <div className="px-4 sm:px-6 py-2.5 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="flex gap-1.5 min-w-max">
                  {CATEGORIES.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-3 py-1.5 rounded-lg font-medium transition-colors whitespace-nowrap text-sm touch-manipulation ${
                        selectedCategory === category.id
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-200'
                      }`}
                    >
                      <span className="mr-1">{category.icon}</span>
                      {category.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
              {isLoadingStock ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                </div>
              ) : stockImages.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {stockImages.map((imageUrl, index) => (
                    <div
                      key={index}
                      className="group relative cursor-pointer rounded-xl overflow-hidden touch-manipulation"
                      onClick={() => handleSelectImage(imageUrl)}
                    >
                      <div className="aspect-video bg-gray-100">
                        <img
                          src={imageUrl}
                          alt={`Stock photo ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 active:bg-black/30 transition-colors flex items-center justify-center">
                        <div className="bg-white rounded-lg px-3 py-1.5 opacity-0 group-hover:opacity-100 active:opacity-100 transition-opacity">
                          <span className="text-xs font-semibold text-gray-900">Use This Image</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Image className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">No images found. Try a different search.</p>
                </div>
              )}
            </div>

            <div className="flex-shrink-0 px-4 sm:px-6 py-3 border-t border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">Images provided by Unsplash</p>
                <a
                  href="https://unsplash.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  Browse Unsplash <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
